<template>
  <div class="ai-knowledge">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__aiKnowledgeManagement") || "AI 知識庫管理" }}</h2>

      <!-- 搜尋列 -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <!-- 左側：類型篩選 + 搜尋框 -->
        <div class="d-flex align-items-center flex-wrap">
          <!-- 類型篩選 -->
          <div class="device-report-select me-2 mb-2">
            <select v-model="searchTypeId" class="form-select rounded-pill" @change="searchKnowledge">
              <option value="">{{ $t("__allTypes") }}</option>
              <option v-for="type in types" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <!-- 搜尋框 -->
          <form @submit.prevent="searchKnowledge" class="mb-2">
            <div class="input-group rounded-pill">
              <input
                v-model="search"
                type="text"
                class="form-control rounded-pill"
                :placeholder="$t('__searchAiKnowledgePlaceholder')"
                aria-label="search"
              />
              <button type="submit" class="btn btn-link px-2 text-dark">
                <i class="bi bi-search"></i>
              </button>
              <button
                v-if="search"
                type="button"
                class="btn btn-primary px-3 ms-1 rounded-pill"
                @click="clearSearch"
              >
                {{ $t('__clearSearch') }}
              </button>
            </div>
          </form>
        </div>

        <!-- 右側：功能按鈕 -->
        <div class="d-flex gap-2 mb-2">
          <button
            @click="showModal()"
            class="fs-5 btn btn-primary px-2 rounded-circle lh-sm"
            :title="$t('__addAiKnowledge')"
          >
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>

      <!-- 知識列表 -->
      <div class="table-responsive bg-white rounded shadow-sm">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th scope="col" width="120">{{ $t('__aiType') }}</th>
              <th scope="col">{{ $t('__aiKnowledgeTitle') }}/{{ $t('__aiKnowledgeQuestion') }}</th>
              <th scope="col">{{ $t('__aiKnowledgeAnswer') }}/{{ $t('__aiKnowledgeContent') }}</th>
              <th scope="col" width="200">{{ $t('__aiTags') }}</th>
              <th scope="col" width="100">{{ $t('__aiKnowledgeTrustScore') }}</th>
              <th scope="col" width="100">{{ $t('__status') }}</th>
              <th scope="col" width="120">{{ $t('__actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('__loading') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="!knowledge.length">
              <td colspan="7" class="text-center py-5 text-muted">
                {{ $t('__noAiKnowledgeData') }}
              </td>
            </tr>
            <tr v-else v-for="item in knowledge" :key="item.id">
              <td>
                <span class="badge bg-info">
                  {{ item.type ? item.type.name : '-' }}
                </span>
              </td>
              <td>{{ item.title || item.question || '-' }}</td>
              <td>
                <div class="text-truncate" style="max-width: 300px" :title="item.answer">
                  {{ item.answer || '-' }}
                </div>
              </td>
              <td>
                <div class="d-flex flex-wrap gap-1">
                  <span v-for="tag in (item.tags || [])" :key="tag" class="badge bg-secondary">
                    {{ tag }}
                  </span>
                </div>
              </td>
              <td>
                <span :class="getTrustScoreClass(item.trust_score)">
                  {{ (item.trust_score * 100).toFixed(0) }}%
                </span>
              </td>
              <td>
                <span v-if="item.is_active" class="badge bg-success">{{ $t('__enabled') }}</span>
                <span v-else class="badge bg-secondary">{{ $t('__disabled') }}</span>
                <span v-if="item.needs_review" class="badge bg-warning ms-1">{{ $t('__needsReview') }}</span>
              </td>
              <td>
                <button
                  @click="showModal(item)"
                  class="btn btn-sm btn-outline-primary me-1"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  @click="deleteKnowledge(item)"
                  class="btn btn-sm btn-outline-danger"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分頁 -->
      <nav v-if="totalPages > 1" class="mt-3">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" @click="changePage(currentPage - 1)">{{ $t('__previousPage') }}</a>
          </li>
          <li
            v-for="page in displayPages"
            :key="page"
            class="page-item"
            :class="{ active: page === currentPage }"
          >
            <a class="page-link" @click="changePage(page)">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" @click="changePage(currentPage + 1)">{{ $t('__nextPage') }}</a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 編輯知識 Modal -->
    <div
      class="modal fade cms-style"
      id="knowledgeModal"
      tabindex="-1"
      aria-hidden="true"
      ref="knowledgeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="saveKnowledge">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ editItem.id ? $t('__editAiKnowledge') : $t('__addAiKnowledge') }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t('__aiType') }}</label>
                <select v-model="editItem.type_id" class="form-select" required>
                  <option value="">{{ $t('__selectAiType') }}</option>
                  <option v-for="type in types" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t('__aiKnowledgeTitle') }}</label>
                <input
                  v-model="editItem.title"
                  type="text"
                  class="form-control"
                  :placeholder="$t('__inputAiTitle')"
                />
              </div>

              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t('__aiKnowledgeQuestion') }}</label>
                <textarea
                  v-model="editItem.question"
                  class="form-control"
                  rows="2"
                  :placeholder="$t('__inputAiQuestion')"
                ></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t('__aiKnowledgeAnswer') }}</label>
                <textarea
                  v-model="editItem.answer"
                  class="form-control"
                  rows="4"
                  :placeholder="$t('__inputAiAnswer')"
                ></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t('__aiTags') }}</label>
                <div class="position-relative">
                  <input
                    v-model="tagsInput"
                    @input="onTagInput"
                    @keydown.enter.prevent="addTagFromSuggestion"
                    @keydown.down.prevent="moveTagSuggestion(1)"
                    @keydown.up.prevent="moveTagSuggestion(-1)"
                    type="text"
                    class="form-control"
                    :placeholder="$t('__inputAiTags')"
                  />
                  <!-- 標籤建議下拉選單 -->
                  <div v-if="tagSuggestions.length > 0" class="dropdown-menu show w-100" style="max-height: 200px; overflow-y: auto;">
                    <button
                      v-for="(tag, index) in tagSuggestions"
                      :key="tag.id"
                      @click="selectTagSuggestion(tag)"
                      class="dropdown-item"
                      :class="{ active: index === selectedSuggestionIndex }"
                      type="button"
                    >
                      {{ tag.display_name || tag.tag_name }}
                      <small class="text-muted ms-2">({{ $t('__aiUsageCount', { count: tag.usage_count }) }})</small>
                    </button>
                  </div>
                </div>
                <!-- 已選擇的標籤 -->
                <div class="mt-2 d-flex flex-wrap gap-2" v-if="selectedTags.length > 0">
                  <span v-for="tag in selectedTags" :key="tag" class="badge bg-primary">
                    {{ tag }}
                    <button @click="removeTag(tag)" type="button" class="btn-close btn-close-white ms-1" style="font-size: 0.7rem;"></button>
                  </span>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form-check">
                    <input
                      v-model="editItem.is_active"
                      class="form-check-input"
                      type="checkbox"
                      id="isActive"
                    />
                    <label class="form-check-label" for="isActive">
                      {{ $t('__enabled') }}
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check">
                    <input
                      v-model="editItem.needs_review"
                      class="form-check-input"
                      type="checkbox"
                      id="needsReview"
                    />
                    <label class="form-check-label" for="needsReview">
                      {{ $t('__needsReview') }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t('__cancel') }}
              </button>
              <button
                type="submit"
                class="btn btn-primary rounded-pill"
              >
                {{ $t('__save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiKnowledge } from '@/http/api/aiKnowledge'
import { Modal } from 'bootstrap'

export default {
  name: 'AiKnowledge',
  data() {
    return {
      knowledge: [],
      types: [],
      loading: false,
      search: '',
      searchTypeId: '',
      currentPage: 1,
      totalPages: 1,
      perPage: 20,
      editItem: {
        type_id: '',
        is_active: true,
        needs_review: false
      },
      tagsInput: '',
      selectedTags: [],
      tagSuggestions: [],
      selectedSuggestionIndex: -1,
      modal: null,
      tagSearchTimer: null
    }
  },
  computed: {
    displayPages() {
      const pages = []
      const maxDisplay = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxDisplay / 2))
      let end = Math.min(this.totalPages, start + maxDisplay - 1)

      if (end - start < maxDisplay - 1) {
        start = Math.max(1, end - maxDisplay + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
  },
  mounted() {
    this.getTypes()
    this.getKnowledge()
    this.modal = new Modal(this.$refs.knowledgeModal)
  },
  methods: {
    async getTypes() {
      try {
        const token = localStorage.getItem('humetrics_token')
        const response = await aiKnowledge.types.get(token)
        if (response.status === 200) {
          this.types = response.data.data || []
        }
      } catch (error) {
        console.error(this.$t('__aiGetTypeFailed'), error)
      }
    },

    async getKnowledge() {
      this.loading = true
      try {
        const token = localStorage.getItem('humetrics_token')
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        }

        if (this.search) params.search = this.search
        if (this.searchTypeId) params.type_id = this.searchTypeId

        const response = await aiKnowledge.get(params, token)
        if (response.status === 200) {
          this.knowledge = response.data.data
          this.totalPages = response.data.last_page
          this.currentPage = response.data.current_page
        }
      } catch (error) {
        console.error(this.$t('__aiGetKnowledgeFailed'), error)
        alert(this.$t('__aiGetDataFailed'))
      } finally {
        this.loading = false
      }
    },

    searchKnowledge() {
      this.currentPage = 1
      this.getKnowledge()
    },

    clearSearch() {
      this.search = ''
      this.searchTypeId = ''
      this.currentPage = 1
      this.getKnowledge()
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.getKnowledge()
    },

    showModal(item = null) {
      if (item) {
        this.editItem = { ...item }
        this.selectedTags = item.tags || []
        this.tagsInput = ''
      } else {
        this.editItem = {
          type_id: '',
          is_active: true,
          needs_review: false
        }
        this.selectedTags = []
        this.tagsInput = ''
      }
      this.tagSuggestions = []
      this.modal.show()
    },

    async saveKnowledge() {
      try {
        const token = localStorage.getItem('humetrics_token')
        const data = { ...this.editItem }

        // 設定標籤
        data.tags = this.selectedTags

        let response
        if (data.id) {
          response = await aiKnowledge.update(data.id, data, token)
        } else {
          response = await aiKnowledge.create(data, token)
        }

        if (response.status === 200 || response.status === 201) {
          alert(data.id ? this.$t('__aiUpdateSuccess') : this.$t('__aiCreateSuccess'))
          this.modal.hide()
          this.getKnowledge()
        } else {
          alert(this.$t('__aiOperationFailed'))
        }
      } catch (error) {
        console.error(this.$t('__saveFailed'), error)
        alert(this.$t('__saveFailed'))
      }
    },

    async deleteKnowledge(item) {
      if (!confirm(this.$t('__aiConfirmDelete', { title: item.title || item.question }))) return

      try {
        const token = localStorage.getItem('humetrics_token')
        const response = await aiKnowledge.delete(item.id, token)

        if (response.status === 200) {
          alert(this.$t('__aiDeleteSuccess'))
          this.getKnowledge()
        } else {
          alert(this.$t('__aiDeleteFailed'))
        }
      } catch (error) {
        console.error(this.$t('__aiDeleteFailed'), error)
        alert(this.$t('__aiDeleteFailed'))
      }
    },

    // 標籤相關方法
    async onTagInput() {
      clearTimeout(this.tagSearchTimer)

      const input = this.tagsInput.trim()

      // 檢查是否輸入逗號
      if (input.includes(',')) {
        const parts = input.split(',')
        const tag = parts[0].trim()
        if (tag && !this.selectedTags.includes(tag)) {
          this.selectedTags.push(tag)
        }
        this.tagsInput = parts.slice(1).join(',').trim()
        this.tagSuggestions = []
        return
      }

      // 搜尋標籤建議
      if (input.length > 0) {
        this.tagSearchTimer = setTimeout(async () => {
          try {
            const token = localStorage.getItem('humetrics_token')
            const response = await aiKnowledge.tags.suggest(input, token)
            if (response.status === 200) {
              this.tagSuggestions = response.data
              this.selectedSuggestionIndex = -1
            }
          } catch (error) {
            console.error(this.$t('__aiGetTagSuggestFailed'), error)
          }
        }, 300)
      } else {
        this.tagSuggestions = []
      }
    },

    selectTagSuggestion(tag) {
      const tagName = tag.tag_name
      if (!this.selectedTags.includes(tagName)) {
        this.selectedTags.push(tagName)
      }
      this.tagsInput = ''
      this.tagSuggestions = []
    },

    addTagFromSuggestion() {
      if (this.selectedSuggestionIndex >= 0 && this.tagSuggestions.length > 0) {
        this.selectTagSuggestion(this.tagSuggestions[this.selectedSuggestionIndex])
      } else if (this.tagsInput.trim()) {
        const tag = this.tagsInput.trim()
        if (!this.selectedTags.includes(tag)) {
          this.selectedTags.push(tag)
        }
        this.tagsInput = ''
        this.tagSuggestions = []
      }
    },

    moveTagSuggestion(direction) {
      if (this.tagSuggestions.length === 0) return

      this.selectedSuggestionIndex += direction

      if (this.selectedSuggestionIndex < 0) {
        this.selectedSuggestionIndex = this.tagSuggestions.length - 1
      } else if (this.selectedSuggestionIndex >= this.tagSuggestions.length) {
        this.selectedSuggestionIndex = 0
      }
    },

    removeTag(tag) {
      const index = this.selectedTags.indexOf(tag)
      if (index > -1) {
        this.selectedTags.splice(index, 1)
      }
    },

    getTrustScoreClass(score) {
      if (score >= 0.8) return 'text-success fw-bold'
      if (score >= 0.5) return 'text-warning'
      return 'text-danger'
    },

    showTypeModal() {
      alert(this.$t('__aiFeatureInDevelopment'))
    },

    showTagModal() {
      alert(this.$t('__aiFeatureInDevelopment'))
    }
  }
}
</script>

<style scoped>
.ai-knowledge {
  min-height: calc(100vh - 80px);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover,
.dropdown-item.active {
  background-color: #f8f9fa;
}
</style>
